// Shared timing for the heartbeat path. One module because these numbers are
// only correct relative to each other, and they have drifted apart before.
//
// History: lektion-data carried ACTIVE_WINDOW_MS = 22s with the comment "2.2x
// heartbeat margin (ping every 10s during a lesson)". The client ping had since
// moved to 20s and the server-side write throttle to 20s, so the worst-case age
// of User.lastSeenWeb was ~40s measured against a 22s window — students who were
// working showed up as inactive in the teacher's matrix and tripped the
// projector's inactivity warning. Found by the 2026-08-11 load test.
//
// The fix is not a bigger magic number: it is deriving the window from the two
// constants that actually determine staleness, so it cannot drift again.

/** Client ping interval while the student's class has a lesson running. */
export const HEARTBEAT_ACTIVE_INTERVAL_MS = 20_000;

/** Client ping interval outside a lesson. */
export const HEARTBEAT_IDLE_INTERVAL_MS = 30_000;

/** The paired phone's heartbeat while a lesson is running and no long poll is waiting (MobileShell). */
export const MOBIL_HEARTBEAT_LEKTION_MS = 10_000;

/** The paired phone's heartbeat outside a lesson and no long poll is waiting (MobileShell). */
export const MOBIL_HEARTBEAT_IDLE_MS = 30_000;

/**
 * The paired phone's heartbeat while its long poll is waiting (MobileShell; the long poll
 * carries the calculator lock, src/lib/mobil-vantan.ts). Bounded by two consumers: lesson
 * activity is logged per MINUTE from this ping, so it must stay under 60 s; and it must
 * stay above HEARTBEAT_WRITE_THROTTLE_MS so every ping writes MobilePairing.lastSeenAt,
 * keeping the worst-case age inside ACTIVE_WINDOW_MS. heartbeat-throttle.test.ts checks both.
 */
export const MOBIL_HEARTBEAT_MED_VANTARE_MS = 45_000;

/**
 * Server-side throttle on User.lastSeenWeb / MobilePairing.lastSeenAt writes. A
 * ping arriving sooner than this after the previous write is answered without
 * writing.
 *
 * 20 s → 40 s (2026-10-04, stress test): at 84 students the two heartbeats were
 * ~9 of the ~12 writes/s the singleton's mutex allows, so every forsok queued
 * behind them (db_skriv p50 ~1 s while the read pool sat at 25 ms). The only
 * consumer of the staleness is the teacher's activity indicator, and
 * ACTIVE_WINDOW_MS below is derived from this constant, so it widens with it
 * (55 s → 75 s): a student who leaves shows as active up to 20 s longer.
 */
export const HEARTBEAT_WRITE_THROTTLE_MS = 40_000;

/**
 * How long heartbeat telemetry (User.lastSeenWeb, MobilePairing.lastSeenAt, LektionAktivitet)
 * may sit in the per-instance queue before it is written (telemetri-ko.ts, plan D / D1,
 * 2026-10-25). The fields carry the beat's own time, but they reach the database up to this
 * much later — so it is part of the worst-case age below, and the most telemetry an
 * instance that dies can take with it. Must stay ≤ 5 s.
 */
export const TELEMETRI_SPOLA_MS = 5_000;

/**
 * Allowance for network latency and scheduler jitter on top of the worst case.
 * A browser tab that was backgrounded and comes back skips a beat, so the real
 * worst case sits slightly above the arithmetic one.
 */
const HEARTBEAT_SLACK_MS = 15_000;

/**
 * How old User.lastSeenWeb may be while the student still counts as active.
 *
 * Worst case: a ping writes, the next ping lands just inside the throttle
 * window and is skipped, and only the one after that writes — and that write
 * then waits up to TELEMETRI_SPOLA_MS in the queue. That is interval +
 * throttle + flush lag, plus slack (75 s → 80 s on 2026-10-25, D1: a student
 * who leaves shows as active up to 5 s longer). Anything smaller reports working students as
 * inactive; the cost of a larger value is only that a student who leaves keeps
 * showing as active a little longer, which is the far cheaper error.
 */
export const ACTIVE_WINDOW_MS =
  HEARTBEAT_ACTIVE_INTERVAL_MS + HEARTBEAT_WRITE_THROTTLE_MS + TELEMETRI_SPOLA_MS + HEARTBEAT_SLACK_MS;
